import { ProductKey, Url } from '@atlassiansox/bxpkit-core';
import { extend } from 'lodash';
import {
  CartServiceContructorType,
  CartServiceInterface,
  HamletClientInterface,
  HamletPricingResponseType,
  HamletProductType
} from '../../model/check-email-page';

export const CartService: CartServiceContructorType = class CartServiceClass
  implements CartServiceInterface
{
  private readonly client: HamletClientInterface;
  private readonly productKeysProp?: string[];
  private products: HamletProductType[];

  constructor({
    productKeys,
    hamletClient
  }: {
    productKeys?: string[];
    hamletClient: HamletClientInterface;
  }) {
    this.client = hamletClient;
    this.productKeysProp = productKeys;
    this.products = [];
  }

  /**
   * Return cart products
   */

  getProducts(): Promise<HamletProductType[]> {
    return new Promise(async (resolve) => {
      if (this.products.length > 0) {
        return resolve(this.products);
      } else {
        this.products = await this._load();
        resolve(this.products);
      }
    });
  }

  /**
   * Loads cart data from Hamlet Pricing
   */

  async _load(): Promise<any> {
    const queryProducts = [
      ProductKey.JIRA_SOFTWARE,
      ProductKey.JIRA_SERVICE_DESK,
      ProductKey.JIRA_CORE,
      ProductKey.JIRA_ROADMAPS,
      ProductKey.JIRA_BONFIRE,
      ProductKey.JIRA_REFAPP,
      ProductKey.CONFLUENCE,
      ProductKey.CONFLUENCE_PLUGIN_QUESTIONS,
      ProductKey.CONFLUENCE_TEAM_CALENDARS
    ];

    const pricingData = await this.client.hamletPricing({
      productKeys: queryProducts,
      licenseType: 'evaluation',
      requestingApplication: 'WAC',
      monthsValid: '1'
    });

    // parse
    const products = this._parseProducts(pricingData);
    return Promise.resolve(products);
  }

  /**
   * Parse products into cart
   */

  _parseProducts(pricingData: HamletPricingResponseType): HamletProductType[] {
    const result: any[] = [];
    if (pricingData?.products?.length === 0) {
      return result;
    }
    const productCatalog = pricingData.products;
    const urlProducts: { [key: string]: number } = {};

    // read unitCount and products query string parameters
    const queryParams = Url.getUrlParams();
    const unitCounts = queryParams?.unitCount
      ? queryParams.unitCount.split(',')
      : [10];
    let queryParamProducts = [];
    // check query string params for products
    if (queryParams?.products) {
      queryParamProducts = queryParams?.products.split(',');
    }
    // if props exist for productKeys, those have priority
    if (this.productKeysProp && this.productKeysProp.length > 0) {
      queryParamProducts = this.productKeysProp;
    }

    // create a map of products/unit counts for each item in querystring
    for (let i = 0, iLen = queryParamProducts.length; i < iLen; i++) {
      const product = queryParamProducts[i];
      let count = 0;

      if (unitCounts.length === 1) {
        if (
          product === ProductKey.JIRA_SOFTWARE ||
          product === ProductKey.CONFLUENCE
        ) {
          count = parseInt(unitCounts[0], 10);
        }
      } else if (unitCounts.length === queryParamProducts.length) {
        count = parseInt(unitCounts[i], 10);
      }

      urlProducts[product] = count;
    }

    // check if product is orderable, if so then add it to the cart
    Object.keys(urlProducts).map((key) => {
      let orderable = null;
      const productUnitCount = urlProducts[key];
      const productHamlet = productCatalog.filter((p) => {
        return p.productKey === key;
      })[0];

      if (productHamlet) {
        if (
          productHamlet.parentKey === 'atlassian.ondemand' ||
          productHamlet.productType === 'HOSTED_ADDON' ||
          productUnitCount !== 0
        ) {
          orderable = productHamlet.orderableItems.filter((item) => {
            return item.unitCount === productUnitCount;
          })[0];
        }
        if (!orderable) {
          orderable =
            productHamlet.monthly.length > 0
              ? productHamlet.monthly[0]
              : productHamlet.orderableItems[0];
        }
      }

      if (productHamlet && orderable) {
        result.push(
          extend({}, orderable, {
            amount: orderable?.renewalAmount,
            mpac: productHamlet.marketplaceAddon === true,
            productKey: productHamlet.productKey,
            name: productHamlet.productDescription,
            saleType: 'EVALUATION'
          })
        );
      }
    });

    return result;
  }
};
